import apiFetch from '@scale/frontend-lib/api-fetch';
import { AuditResultV2, PerTurnFeedback } from '@scale/shared/defaults';
import { IAttemptAuditJson } from '@scale/shared/types/speedAudit';
import { IUserJson } from '@scale/shared/types/user';
import { AttemptAuditResults, SaveEditRequest } from '../SpeedAudit/types';
import {
  ChatBulkAuditTaskAction,
  AttemptAuditMaxClaimTimeResponse,
  CreateAuditBatchResponse,
} from '@scale/shared/types/corp/attemptAudit';
import { ChatBulkAuditObjectType } from '@scale/genai-compass-shared/cards/chat_bulk_audit';
import { QueryClient } from '@tanstack/react-query-v4';
import { QAOperationJson } from '@scale/shared/types/qaOperation/qaOperation';

export interface UserTaskInfo {
  attempter: IUserJson;
  sbqPercent: number;
  sbqRejectedAttempts?: number;
  sbqTotalAttempts?: number;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

export async function updateAttemptAudit({
  attemptAuditId,
  grade,
  errorCategories,
  feedback,
  taskerScore,
  taskerId,
  publicTaskerFeedback,
  privateTaskerFeedback,
  taskAction,
  ignored,
  sendToReviewLevel,
  maxScoreDisputed,
  perTurnFeedback,
  internalFeedback,
  isFromDispute = false,
}: {
  attemptAuditId: string;
  grade?: AuditResultV2;
  errorCategories?: string[];
  feedback?: string;
  taskerScore?: AuditResultV2;
  taskerId?: string;
  publicTaskerFeedback?: string;
  privateTaskerFeedback?: string;
  taskAction?: ChatBulkAuditTaskAction;
  ignored?: boolean;
  sendToReviewLevel?: string;
  maxScoreDisputed?: boolean;
  perTurnFeedback?: PerTurnFeedback;
  internalFeedback?: string;
  isFromDispute?: boolean;
}) {
  await apiFetch(null, `corp-api/chatBulkAudit/attemptAudit/${attemptAuditId}`, {
    method: 'PUT',
    body: {
      grade,
      errorCategories,
      feedback,
      taskerScore,
      taskerId,
      publicTaskerFeedback,
      privateTaskerFeedback,
      taskAction,
      ignored,
      sendToReviewLevel,
      maxScoreDisputed,
      perTurnFeedback,
      internalFeedback,
      isFromDispute,
    },
  });
}

export async function getAttemptAuditById(attemptAuditId: string): Promise<IAttemptAuditJson> {
  return await apiFetch<IAttemptAuditJson>(
    null,
    `corp-api/chatBulkAudit/attemptAudit/${attemptAuditId}/details`,
    {
      method: 'GET',
    },
  );
}

export async function getProjectRoleForUser(
  projectId: string | undefined,
  userId: string | undefined,
  roles: string[] | undefined,
) {
  if (!projectId || !userId) {
    return undefined;
  }
  let query;
  if (roles) {
    query = roles.map(role => `roles=${role}`).join('&');
  } else {
    query = '';
  }
  const res = await apiFetch(
    null,
    `corp-api/chatBulkAudit/projectRole/${projectId}/${userId}?${query}`,
    {
      method: 'GET',
    },
  );
  return res;
}

export async function getAttemptAuditResponse(
  attemptAuditId: string | undefined,
): Promise<IAttemptAuditJson | undefined> {
  if (!attemptAuditId) {
    return undefined;
  }
  return await apiFetch<IAttemptAuditJson>(
    null,
    `corp-api/chatBulkAudit/attemptAudit/${attemptAuditId}/response`,
    {
      method: 'GET',
    },
  );
}

export async function getRelatedQaOperationForAuditBatch(
  auditBatchId: string,
): Promise<QAOperationJson | undefined> {
  return await apiFetch(
    null,
    `corp-api/chatBulkAudit/relatedQaOperationForAuditBatch/${auditBatchId}`,
  );
}

export async function fetchUserTaskInfo(
  contributorId: string,
  project?: string,
  reviewLevel?: number,
): Promise<UserTaskInfo> {
  const { attempter, sbqPercent, sbqRejectedAttempts, sbqTotalAttempts } = await apiFetch(
    null,
    `corp-api/chatBulkAudit/attempter/${contributorId}`,
    {
      method: 'GET',
      query: {
        project,
        reviewLevel,
      },
    },
  );
  return {
    attempter,
    sbqPercent,
    sbqRejectedAttempts,
    sbqTotalAttempts,
  };
}

export async function getAttemptResponse(attemptId: string | undefined) {
  const res = await apiFetch(null, `corp-api/chatBulkAudit/attemptAudit/attempt/${attemptId}`, {
    method: 'GET',
  });
  return res.response;
}

export async function getAttemptAuditMaxClaimTimes(auditBatchId: string | undefined) {
  if (!auditBatchId) return undefined;
  return await apiFetch<AttemptAuditMaxClaimTimeResponse>(
    null,
    `corp-api/chatBulkAudit/${auditBatchId}/config/maxClaimTimes`,
    {
      method: 'GET',
    },
  );
}

export async function submitBulkChatAudit({
  auditBatchId,
  attemptAudits,
  user,
  auditFeedback,
}: {
  auditBatchId: string;
  attemptAudits: AttemptAuditResults[];
  user: IUserJson;
  auditFeedback: string;
}) {
  return await apiFetch(null, `corp-api/chatBulkAudit/complete/${auditBatchId}`, {
    method: 'POST',
    body: {
      attemptAudits,
      userId: user._id,
      auditFeedback,
    },
  });
}

export async function getAuditErrorCategories(
  projectId: string | undefined,
  displayStandardizedErrorCategories?: boolean,
): Promise<string[]> {
  if (!projectId) {
    return [];
  }
  const fetchUrl = displayStandardizedErrorCategories
    ? `corp-api/auditErrorCategories/serializedErrorCategories/${projectId}`
    : `corp-api/attemptAudit/errorCategories/${projectId}`;
  const { errorCategories } = await apiFetch(null, fetchUrl);
  return errorCategories;
}

export async function getAuditErrorCategoriesToMaxScoreMap(projectId: string | undefined): Promise<{
  [errorCategory: string]: number;
}> {
  if (!projectId) {
    return {};
  }
  const { errorCategoriesToMaxScoreMap } = await apiFetch(
    null,
    `corp-api/auditErrorCategories/errorCategories/${projectId}/serializedErrorCategoriesToMaxScoreMap`,
  );
  return errorCategoriesToMaxScoreMap;
}

export async function getTasksForAttempter(
  projectId: string,
  attempterId: string,
  limit: number,
): Promise<{
  attemptIds: string[];
}> {
  const { attemptIds } = await apiFetch(
    null,
    `corp-api/chatBulkAudit/tasks/${projectId}/attempters/${attempterId}?limit=${limit}`,
    {
      method: 'GET',
    },
  );
  return { attemptIds };
}

export async function createChatBulkAuditForContributors({
  projectId,
  workerIds,
  auditName,
  shouldClaimTasks,
  dateRange,
  limit,
  reviewLevels,
}: {
  projectId: string;
  workerIds: string[];
  auditName: string;
  shouldClaimTasks: boolean;
  dateRange: { startDate: Date; endDate: Date };
  limit: number;
  reviewLevels: string[];
}) {
  return await apiFetch<CreateAuditBatchResponse>(null, `corp-api/chatBulkAudit/batch`, {
    method: 'POST',
    body: {
      projectId,
      name: auditName,
      objectIds: workerIds,
      objectType: ChatBulkAuditObjectType.WORKER,
      claimTasks: shouldClaimTasks,
      dateRange,
      limit,
      reviewLevels,
    },
  });
}

export async function createChatBulkAudit({
  projectId,
  auditName,
  taskIds,
  shouldClaimTasks = false,
}: {
  projectId: string;
  auditName: string;
  taskIds: string[];
  shouldClaimTasks?: boolean;
}) {
  return await apiFetch<CreateAuditBatchResponse>(null, `corp-api/chatBulkAudit/batch`, {
    method: 'POST',
    body: {
      projectId,
      name: auditName,
      objectIds: taskIds,
      claimTasks: shouldClaimTasks,
    },
  });
}

export async function getAuditGradingInstructionsInternalUrl(projectId?: string): Promise<
  | {
      internalUrl?: string;
      validationError?: string;
    }
  | undefined
> {
  if (!projectId) {
    return undefined;
  }
  const { internalUrl, validationError } = await apiFetch(
    null,
    `corp-api/chatBulkAudit/gradingInstructionsInternalUrl/${projectId}`,
  );
  return {
    internalUrl,
    validationError,
  };
}

export async function getAttempterAuditScores(
  projectId?: string,
  attempterId?: string,
): Promise<
  | {
      auditAverage: number;
      numAuditedAttempts: number;
    }
  | undefined
> {
  if (!attempterId || !projectId) {
    return undefined;
  }
  const { auditAverage, numAuditedAttempts } = await apiFetch(
    null,
    `corp-api/chatBulkAudit/attempter/${attempterId}/auditScores?project=${projectId}`,
  );
  return {
    auditAverage,
    numAuditedAttempts,
  };
}

export async function saveResponseEdit(body: SaveEditRequest) {
  return await apiFetch(null, `/corp-api/chatBulkAudit/attempt/edit`, {
    body,
    method: 'POST',
  });
}

export async function undoResponseEdits(attemptAuditId: string) {
  return await apiFetch(null, `/corp-api/chatBulkAudit/attempt/undoEdits/${attemptAuditId}`, {
    method: 'DELETE',
  });
}

export async function loadFormattedJson(attemptAuditId: string) {
  return await apiFetch(
    null,
    `/corp-api/chatBulkAudit/attempt/loadFormattedJson/${attemptAuditId}`,
    {
      method: 'POST',
    },
  );
}

export async function getAuditorPermissions() {
  return await apiFetch(null, `/corp-api/chatBulkAudit/auditor/permissions`, {
    method: 'GET',
  });
}
